// nameMask.js — single source of truth for the "Hide names" admin toggle.
//
// What it does: a "Pharmace all" admin clicks a toggle button. After that,
// every pharmacy name and every staff name on screen becomes its first two
// characters followed by six asterisks. The state is persisted in
// localStorage and synchronised between components via a custom DOM event,
// so toggling on one screen takes effect on every other screen immediately.
//
// What it does NOT touch: API request bodies, sort/group/filter keys, email
// subjects/bodies, localStorage reads — these stay real so the app keeps
// working and outbound communications keep the correct names.

import React, { useEffect, useState } from "react";

// ----- Pure helpers (safe to call anywhere) -----------------------------------

export function maskName(name) {
  if (!name) return name;
  const s = String(name);
  return (s.length <= 1 ? s : s.slice(0, 1)) + "******";
}

export function displayName(name, masked) {
  return masked ? maskName(name) : name;
}

// Best-effort masking of free-form text that may embed a known staff name.
// Used for things like Action Centre alert.summary / alert.insightTitle where
// the server has rendered the staff name into prose. Pass any known staff
// name(s) from the same alert object; we replace every occurrence (case-
// insensitive) with the masked form.
export function maskTextWithNames(text, names, masked) {
  if (!masked || !text) return text;
  const list = (Array.isArray(names) ? names : [names]).filter(Boolean);
  if (list.length === 0) return text;
  let out = String(text);
  for (const raw of list) {
    const n = String(raw).trim();
    if (n.length < 2) continue;
    const esc = n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    out = out.replace(new RegExp(esc, "gi"), maskName(n));
  }
  return out;
}

// ----- State plumbing ---------------------------------------------------------

const STORAGE_KEY = "maskPharmacyNames";  // legacy key name kept for back-compat
const EVENT_NAME  = "pharmacy-mask-changed";

// Masking is an admin-only ("Pharmace all") feature. We gate the *read* of the
// flag on admin status so a flag left over from a previous admin session can
// never keep names hidden for a non-admin user who has no toggle to undo it.
function isMaskAdmin() {
  try { return localStorage.getItem("organisationName") === "Pharmace all"; }
  catch (_) { return false; }
}

function readMaskFlag() {
  try {
    if (!isMaskAdmin()) return false;            // non-admins are never masked
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch (_) { return false; }
}

export function setMaskFlag(next) {
  try { localStorage.setItem(STORAGE_KEY, next ? "true" : "false"); } catch (_) {}
  try { window.dispatchEvent(new Event(EVENT_NAME)); } catch (_) {}
}

export function useMaskNames() {
  const [masked, setMasked] = useState(readMaskFlag);
  useEffect(() => {
    const handler = () => setMasked(readMaskFlag());
    window.addEventListener(EVENT_NAME, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(EVENT_NAME, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);
  return masked;
}

// Back-compat alias for the original hook name used in Mockupp.js.
export const useMaskPharmacyNames = useMaskNames;

// ----- The toggle button itself -----------------------------------------------

export function MaskToggleButton({ style }) {
  const viewerOrg = (typeof window !== "undefined" && localStorage.getItem("organisationName")) || "";
  const isAdmin = viewerOrg === "Pharmace all";
  const masked = useMaskNames();
  if (!isAdmin) return null;
  return (
    <button
      onClick={() => setMaskFlag(!masked)}
      title={masked
        ? "Show real pharmacy and staff names"
        : "Hide pharmacy and staff names (e.g. for demos / screen sharing)"}
      style={{
        padding: "5px 10px",
        background: masked ? "#fbbf24" : "#fff",
        border: "1px solid #d1d5db",
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 500,
        cursor: "pointer",
        color: masked ? "#78350f" : "#374151",
        whiteSpace: "nowrap",
        ...(style || {})
      }}
    >
      {masked ? "🔒 Names hidden" : "👁 Hide names"}
    </button>
  );
}